import { useEffect, useRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { IconAlert, IconCheck, IconClose, IconInfo, IconSpinner } from './icons.js';

/** Piezas de interfaz compartidas. Objetivo tactil de 44 px en todo lo pulsable. */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  loading?: boolean;
  icon?: ReactNode;
};

const VARIANTS: Record<string, string> = {
  primary: 'bg-marca text-white hover:bg-marca-oscura disabled:bg-neutral-300',
  secondary: 'bg-papel-suave text-tinta border border-borde hover:bg-white',
  ghost: 'bg-transparent text-tinta hover:bg-papel-suave',
  danger: 'bg-error text-white hover:opacity-90',
};

export function Button({ variant = 'primary', loading, icon, children, className = '', ...props }: ButtonProps) {
  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className={`toque inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${VARIANTS[variant]} ${className}`}
    >
      {loading ? <IconSpinner size={16} /> : icon}
      {children}
    </button>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-borde bg-white p-4 ${className}`}>{children}</div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium tracking-wide text-tinta-suave uppercase">{label}</span>
      {children}
      {hint ? <span className="block text-xs text-tinta-suave">{hint}</span> : null}
    </label>
  );
}

export function NumberInput({
  value,
  onChange,
  step = 0.1,
  suffix,
  ...rest
}: {
  value: number;
  onChange: (value: number) => void;
  step?: number;
  suffix?: string;
  min?: number;
  max?: number;
}) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        inputMode="decimal"
        value={Number.isFinite(value) ? Math.round(value * 10) / 10 : 0}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="toque w-full rounded-lg border border-borde px-3 py-2 text-sm tabular-nums"
        {...rest}
      />
      {suffix ? <span className="text-xs text-tinta-suave">{suffix}</span> : null}
    </div>
  );
}

export function Banner({
  tone,
  title,
  children,
}: {
  tone: 'error' | 'warning' | 'info' | 'ok';
  title: string;
  children?: ReactNode;
}) {
  const styles: Record<string, { border: string; icon: ReactNode }> = {
    error: { border: 'border-error bg-red-50', icon: <IconAlert className="text-error" /> },
    warning: { border: 'border-aviso bg-amber-50', icon: <IconAlert className="text-aviso" /> },
    info: { border: 'border-borde bg-papel-suave', icon: <IconInfo /> },
    ok: { border: 'border-ok bg-emerald-50', icon: <IconCheck className="text-ok" /> },
  };
  const style = styles[tone]!;
  return (
    <div className={`flex gap-3 rounded-lg border p-3 ${style.border}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span className="mt-0.5 shrink-0">{style.icon}</span>
      <div className="min-w-0 text-sm">
        <p className="font-medium">{title}</p>
        {children ? <div className="mt-1 text-tinta-suave">{children}</div> : null}
      </div>
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 p-8 text-sm text-tinta-suave">
      <IconSpinner />
      {label ? <span>{label}</span> : null}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-borde p-8 text-center">
      <p className="font-medium">{title}</p>
      {children ? <div className="mt-2 text-sm text-tinta-suave">{children}</div> : null}
    </div>
  );
}

/** Hoja inferior deslizante: el patron de panel en movil. */
export function Sheet(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /**
   * Tambien en pantalla grande, presentada como dialogo centrado.
   *
   * Para lo que NO tiene sitio en las barras laterales: las formas, las
   * plantillas y la biblioteca de imagenes. Sin esto, en un ordenador se abrian
   * y el CSS las escondia.
   */
  enEscritorio?: boolean;
}) {
  const { open, onClose, title, children } = props;
  /*
    Escape cierra, y el fondo deja de moverse mientras la hoja esta abierta.

    El efecto va ANTES del `return null`: un hook detras de un retorno
    condicional cambia el orden de los hooks entre renders.

    Sin esto, la unica forma de cerrar era acertar en el fondo translucido —que
    no parece pulsable— y quien navega con teclado se quedaba atrapado dentro.
  */
  /*
    `onClose` se lee de una REF, no de la dependencia del efecto.

    Quien abre una hoja la abre con `onClose={() => setSheet(null)}`, que es una
    función nueva en cada render. Con ella en las dependencias, el editor
    —que se repinta en cada cambio del documento— desmontaba y volvía a montar
    este manejador decenas de veces por segundo, y una tecla que cayera en el
    hueco se perdía. Con la ref, el manejador se registra UNA vez y siempre
    llama a la función vigente.
  */
  const cerrarRef = useRef(onClose);
  cerrarRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const alPulsar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') cerrarRef.current();
    };
    const overflowPrevio = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', alPulsar);
    return () => {
      window.removeEventListener('keydown', alPulsar);
      window.document.body.style.overflow = overflowPrevio;
    };
  }, [open]);

  if (!open) return null;

  /*
    EN ESCRITORIO TAMBIÉN, cuando lo que hay dentro no está en otro sitio.

    Esta hoja nació solo para el móvil —de ahí el `lg:hidden`— porque en pantalla
    grande las capas, las propiedades y los avisos viven en las barras laterales.
    Pero luego entraron aquí las formas, las plantillas y la biblioteca de
    imágenes, que NO tienen equivalente lateral: en un ordenador, pulsar «Forma»
    abría una hoja que el CSS escondía y no pasaba nada.

    Con `enEscritorio` la misma hoja se presenta centrada, que es lo que espera
    quien usa un ratón, y pegada abajo en el móvil, que es lo que alcanza el
    pulgar.
  */
  const centrada = props.enEscritorio ?? false;
  return (
    <div
      className={`fixed inset-0 z-40 flex ${
        centrada ? 'items-end lg:items-center lg:justify-center' : 'items-end lg:hidden'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button className="absolute inset-0 bg-black/30" onClick={onClose} aria-label="Cerrar" />
      <div
        className={`safe-bottom relative max-h-[75vh] w-full overflow-y-auto rounded-t-2xl bg-white p-4 shadow-2xl ${
          centrada ? 'lg:max-h-[80vh] lg:max-w-lg lg:rounded-2xl' : ''
        }`}
      >
        {/* El tirador solo tiene sentido donde se arrastra: en el móvil. */}
        <div className={`mx-auto mb-3 h-1 w-10 rounded-full bg-borde ${centrada ? 'lg:hidden' : ''}`} />
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">{title}</h2>
          {/* Cierre EXPLICITO: el fondo translucido no se lee como pulsable. */}
          <button onClick={onClose} className="toque -mr-2 rounded-lg p-2" aria-label="Cerrar">
            <IconClose />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'ok' | 'warning' | 'error'; children: ReactNode }) {
  const tones: Record<string, string> = {
    neutral: 'bg-papel-suave text-tinta-suave',
    ok: 'bg-emerald-50 text-ok',
    warning: 'bg-amber-50 text-aviso',
    error: 'bg-red-50 text-error',
  };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}
